import { Component, ElementRef, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

// Port fiel do DatePicker do DnaDirect (dashboard) — calendário custom, dia em quadrado arredondado.
@Component({
  selector: 'dna-datepicker',
  templateUrl: './dna-datepicker.component.html',
  styleUrls: ['./dna-datepicker.component.scss'],
  standalone: false,
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => DnaDatepickerComponent),
    multi: true
  }]
})
export class DnaDatepickerComponent implements ControlValueAccessor {

  @Input() placeholder = 'dd/mm/aaaa';
  @Input() disabled = false;

  value = '';            // dd/mm/aaaa (modelo do ValidaNFE)
  open = false;
  viewDate = new Date();

  readonly weekDays = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];
  readonly months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  private onChange: (v: any) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private el: ElementRef) {}

  writeValue(v: any): void { this.value = v || ''; this.syncView(); }
  registerOnChange(fn: any): void { this.onChange = fn; }
  registerOnTouched(fn: any): void { this.onTouched = fn; }
  setDisabledState(isDisabled: boolean): void { this.disabled = isDisabled; }

  get monthLabel(): string {
    return `${this.months[this.viewDate.getMonth()]} ${this.viewDate.getFullYear()}`;
  }

  get cells(): (number | null)[] {
    const year = this.viewDate.getFullYear();
    const month = this.viewDate.getMonth();
    const firstWeekday = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const result: (number | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) result.push(null);
    for (let d = 1; d <= daysInMonth; d++) result.push(d);
    return result;
  }

  isToday(d: number | null): boolean {
    if (!d) return false;
    const t = new Date();
    return d === t.getDate()
      && this.viewDate.getMonth() === t.getMonth()
      && this.viewDate.getFullYear() === t.getFullYear();
  }

  isSelected(d: number | null): boolean {
    if (!d) return false;
    const sel = this.parse(this.value);
    return !!sel && d === sel.getDate()
      && this.viewDate.getMonth() === sel.getMonth()
      && this.viewDate.getFullYear() === sel.getFullYear();
  }

  toggle(): void {
    if (this.disabled) return;
    this.open = !this.open;
    if (this.open) this.syncView();
  }

  openCalendar(): void {
    if (this.disabled) return;
    this.open = true;
    this.syncView();
  }

  prevMonth(event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
  }

  nextMonth(event: MouseEvent): void {
    event.stopPropagation();
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
  }

  selectDay(d: number | null): void {
    if (!d) return;
    const date = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), d);
    this.value = this.format(date);
    this.onChange(this.value);
    this.onTouched();
    this.open = false;
  }

  onText(v: string): void {
    this.value = v;
    this.onChange(v);
    this.syncView();
  }

  private format(date: Date): string {
    const p = (n: number) => `${n}`.padStart(2, '0');
    return `${p(date.getDate())}/${p(date.getMonth() + 1)}/${date.getFullYear()}`;
  }

  private parse(v: string): Date | null {
    if (!v || v.length < 10) return null;
    const [d, m, y] = v.split('/').map(Number);
    if (!d || !m || !y) return null;
    return new Date(y, m - 1, d);
  }

  private syncView(): void {
    const sel = this.parse(this.value);
    if (sel) this.viewDate = new Date(sel.getFullYear(), sel.getMonth(), 1);
  }

  @HostListener('document:click', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.el.nativeElement.contains(e.target)) this.open = false;
  }
}
